import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Grid, Tab, Card, Button, Theme, Typography, DialogContent, DialogActions, alpha, Box } from "@mui/material";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";

// EvenGroups Field
import { PairingEventTable } from "./PairingEventTable";

import { OcTabs } from "src/components/OcTabs/OcTabs";
import { validatePositionOverrides } from "./EventTableComponents/FlightPositionOverride";
import EditRounded from "@mui/icons-material/EditRounded";
// Tabs
import { PairingNotesTab } from "./Tabs/PairingNotesTab";
import { CrewPositionsTab } from "./Tabs/CrewPositionsTab";
import { NonFlyingAssignmentsTab } from "./Tabs/NonFlyingAssignmentsTab";
import { PositioningFlightsTab } from "./Tabs/PositioningFlightsTab";
// Styles
import classNames from "classnames";
import _ from "lodash";
// Form imports
import {
  IPairing,
  IPairingLogicModifier,
  IPositioningFlight,
  IFullPairingPosition,
  INonFlyingAssignment,
  IPairingEventGroup,
  IFlight,
  IPairingHotel,
  IDraftPairingResponse,
  IStatusCheckIssue,
  isINonFlyingAssignment,
  IPlannedCreditMap,
  getDefaultPlannedCreditMap,
  ICalculatePlannedCreditResult,
  IPairingRest,
  restPeriodMatches,
} from "@vmosolutions/oc-interfaces";
import { IPairingEvent } from "./PairingEventTable";
// Redux actions
import { useSelector, useDispatch } from "react-redux";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
// Default form values.
import { initialValues as newNonFlyingAssignment } from "./Forms/NonFlyingAssignmentForm";
import { initialValues as newPositioningFlight } from "./Forms/PositioningFlightForm";
// Create pairing imports
import moment from "moment-timezone";
import { IPairingEditorEventGroup, defaultEventGroup } from "./PairingEventTable";
import { LogicModifiersTab } from "./Tabs/LogicModifiersTab";
// Pairing Display components
import { ShortCodeChip } from "../../ShortCodes/ShortCodeChip";
// API calls
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  addPairingEditor,
  removePairingEditor,
  removeTemporaryPairing,
  setSelectedCrewEvents,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { ModalButton, ModalList, ModalTitle } from "src/components/DialogModal/DialogModalComponents";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { CopyToClipboard } from "src/pages/TimelineEventEditors/helpers";
import { WarningModal } from "src/components/ModalComponents/WarningModal";
import { updateTabNodeByWindowIdAndProps } from "src/redux/actions/Mosaic.Actions";

interface IPairingEditorWindowProps {
  id: string;
  path: string[];
  windowProps: {
    pairingId: string;
    crewMember?: ICrewMember; // Passed when new pairings are created from crew option menu.
    selectedTime?: any;
    flights?: IFlight[]; // Passed when new pairings created from timeline.
  };
  setCloseWarning: (warning: string) => void;
}

export interface IEmbeddedFormState {
  pairingNotesFormDirty: boolean;
  nonFlyingFormDirty: boolean;
  posFlightFormDirty: boolean;
  logicModifierFormDirty: boolean;
}
export const embeddedFormDefaultState: IEmbeddedFormState = {
  pairingNotesFormDirty: false,
  nonFlyingFormDirty: false,
  posFlightFormDirty: false,
  logicModifierFormDirty: false,
};

// Create formik form type.
export type IPairingForm = IPairing;

const createTempPairing = (info: Partial<IPairing>): any => {
  const defaults: IPairing = {
    _id: undefined,
    start: undefined,
    end: undefined,
    origin: "",
    originTimezoneId: "",
    originCountry: "",
    destination: "",
    destinationTimezoneId: "",
    destinationCountry: "",
    baseICAO: undefined,
    batchId: undefined,
    checkInOutOverrides: [],
    pairingNotes: [],
    isBiddable: false,
    positions: [],
    eventGroups: [],
    positionOverrides: [],
    hotels: [],
    plannedCredit: undefined,
    splitFromPairing: undefined,
    createdBy: undefined,
    foreignRef: [],
    logicModifiers: [],
  };
  return { ...defaults, ...info };
};

// Merge a formatted draft (including rest periods) into the current pairing data.
// Null when the request fails or returns an error, so callers never store events the backend could not place.
export const mergeDraft = async (
  pairing: IPairing,
  dispatch?: INotifications["dispatch"]
): Promise<IDraftPairingResponse | null> => {
  const response = await AmplifyAPIWrapper.Post<IDraftPairingResponse>(
    "/draftPairing",
    pairing,
    dispatch ? { dispatch, errorMessage: "Error: Unable to validate the pairing changes" } : undefined
  );
  // Note "error" is different from the array shown to users: "errors". This checks for the HTTP-level error.
  if (!response?.pairing || "error" in response) {
    return null;
  }
  return {
    pairing: response.pairing.start && response.pairing.end ? response.pairing : pairing,
    errors: response.errors ?? [],
  };
};

const hasTimes = (flight: IFlight): boolean =>
  (flight?.actualOut ?? flight?.scheduledOut) != null && (flight?.actualIn ?? flight?.scheduledIn) != null;

// Some chips drag a reduced flight without times, so those are swapped for the stored flight before drafting.
// Null if one can't be loaded.
export const loadMissingFlights = async (eventGroups: IPairingEventGroup[]): Promise<IPairingEventGroup[] | null> => {
  const groups = await Promise.all(
    eventGroups.map(async (group) => ({
      ...group,
      flights: await Promise.all(
        (group.flights ?? []).map(async (flight) =>
          hasTimes(flight) ? flight : AmplifyAPIWrapper.Get<IFlight>(`/flights/${flight?._id}`)
        )
      ),
    }))
  );
  return groups.every((group) => group.flights.every(hasTimes)) ? groups : null;
};

const pairingEditorWindowStyles = (theme: Theme): object => ({
  "&.pairing_editor_window": {
    height: "calc(100% - 10px)",
    width: "100%",
    "& #editorBody": {
      width: "100%",
      height: "100%",
      display: "flex",
      position: "relative",
      "& .content_container": {
        display: "flex",
        height: "100%",
        flexFlow: "column", // content will always display as column.
        alignContent: "flex-start",
        "&.split": {
          flexWrap: "nowrap",
          flexFlow: "unset",
          "& .content_pairing_tabs": {
            maxHeight: "unset",
          },
          "& .content_pairing_table": {
            marginTop: "43px", // Adds margin to the pairing table when it's table side by side.
          },
        },

        "& .content_pairing_tabs": {
          margin: "10px 10px 0px 10px",
          "& .form_actions": {
            display: "flex",
            alignItems: "center",
            "& .shortCode_chip": {
              marginRight: "5px",
            },
            "& button": {
              marginRight: "5px",
              padding: "4px 8px",
              fontSize: 12,
              fontWeight: 400,
              "&.larger": {
                padding: "2px 8px",
                fontSize: 12,
              },
              "&.smaller": {
                padding: "0px 4px",
                fontSize: 10,
              },
            },
          },
          "& .form_tabs": {
            "& .form-tab.dirty.MuiTab-wrapped .MuiSvgIcon-root": { display: "flex" },
            "& .form-tab.MuiTab-wrapped": {
              display: "flex",
              flexDirection: "row-reverse", // so that edit icon appears to the right of the button text.
              "& .MuiSvgIcon-root": {
                display: "none",
                width: 14,
                height: 14,
                border: "1px solid",
                borderRadius: "3px",
                margin: "0px 5px",
              },
            },
          },
        },

        "& .assign_flights_from_timeline": {
          margin: "8px 15px 0px 15px",
          "& button": {
            margin: "5px",
            padding: "4px 8px",
            fontSize: 12,
            fontWeight: 400,
            "&.larger": {
              padding: "2px 8px",
              fontSize: 14,
            },
            "&.smaller": {
              padding: "0px 4px",
              fontSize: 10,
            },
          },
        },

        "& .content_pairing_table": {
          display: "flex",
          margin: "10px 10px 0px 10px",
          flexGrow: 1, // Occupy remaining available vertical space.
          overflowY: "auto", // Apply scrollbar as needed.
          "& .pairing_summary_header": {
            display: "flex",
            flexWrap: "wrap",
            "& .form_header": {
              display: "flex",
              alignItems: "center",
              padding: "2px 10px",
              "& .form_title": {
                color: theme.palette.primary.contrastText,
                display: "flex",
                padding: 0,
              },
              "& .edit_icon": {
                border: "1px solid",
                borderRadius: "3px",
                width: 20,
                height: 20,
                margin: "5px",
                display: "none",
                "&.show": { display: "block" },
              },
              "& .form_subtitle": {
                color: theme.custom.forms.labels,
                margin: "0px 10px",
                fontSize: 14,
                height: "1.2em",
                alignSelf: "center",
              },
            },
          },
        },
      },
    },
    "& .pairing_cards": {
      padding: "5px",
      borderRadius: "0px 5px 5px 5px",
      backgroundColor: theme.custom.timeline.contextListBackground,
      "&.eventgroups_card": {
        borderRadius: "5px",
      },
    },
  },
  "&.tab_styles": {
    height: "100%",
    width: 868,
    position: "relative",
    "& .event_actions": {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      "& button.action_btn": {
        fontSize: 12,
        fontWeight: 300,
        padding: "0px 10px 0px 5px",
        minWidth: 20,
        "& svg": {
          width: 16,
          height: 16,
          margin: "5px",
        },
      },
    },
    // Crew Position Fields
    "& .pairing_notes": {
      backgroundColor: theme.custom.timeline.contextListBackground,
      "& .pairing_note": {
        color: theme.custom.forms.inputText,
        margin: "5px 0px",
        "& .pairing_note_textfield": {
          "& label": { width: "180px" },
        },
      },
    },
    "& .webgl_pairing": {
      display: "flex",
      width: "calc(100% - 20px)",
      margin: "0px 10px 10px 10px",
      borderRadius: "5px",
      backgroundColor: theme.palette.background.paper,
    },
  },
  "& .ModalDialogStyle": {
    marginTop: "-36px",
    display: "flex",
    justifyContent: "center",
    overflow: "hidden",
    alignItems: "center",
    position: "absolute",
    zIndex: 1300,
    width: "100%",
    height: "100%",
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    "& .modal_content": {
      backgroundColor: theme.palette.background.paper,
      display: "flex",
      flexDirection: "column",
      alignContent: "center",
      justifyContent: "center",
      borderRadius: "3px",
      padding: "10px",
      margin: "48px",
      maxHeight: "calc(100% - 96px)",
      maxWidth: 600,
      position: "relative",
      boxShadow:
        "0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)",
      "& .slider-container": {
        padding: "0px 10px 0px 10px",
      },
      "& .modal_status": {
        fontSize: 12,
        fontWeight: 300,
        color: theme.palette.primary.contrastText,
        paddingLeft: "25px",
      },
      "& .modal_list": {
        padding: "16px 24px",
        "& .list_container": {
          display: "block",
        },
      },
    },
  },
  "& .eventId": {
    margin: "0 !important",
    padding: "5px 15px !important",
    color: theme.custom.forms.labels,
    opacity: 0.25,
    "&:hover": {
      opacity: 0.75,
    },
    "& .label": { fontSize: 12 },
    "& .icon": { height: 15, width: 15, cursor: "pointer", marginLeft: "5px" },
  },
});

/**
 * Function that returns the ground indexes of any dirty rest periods
 * @param formProps - pairing forms
 * @returns number[]
 */
const restPeriodIsDirty = (pairing: IPairing, tempPairing: IPairing): number[] => {
  const dirtyIndexes: number[] = [];

  // HOTELS
  const oldHotels = pairing?.hotels;
  const newHotels = tempPairing?.hotels;
  if (!oldHotels || !newHotels) {
    return dirtyIndexes;
  }

  const originalRestByIndex = new Map<number, IPairingRest>();
  pairing?.eventGroups?.forEach((eg, egIndex) => {
    eg.restPeriods?.forEach((rest) => {
      const restIndex = rest.eventGroup ?? egIndex;
      if (restIndex != null) {
        originalRestByIndex.set(restIndex, rest);
      }
    });
  });

  // RESTPERIODS
  const newRestPeriodGroups = tempPairing?.eventGroups?.filter((group) => group.restPeriods.length > 0);
  newRestPeriodGroups.forEach((newEg) => {
    const newRest = newEg.restPeriods[0];
    let oldIndex: number | undefined = undefined;

    for (const [restIndex, origRest] of originalRestByIndex.entries()) {
      if (restPeriodMatches(origRest, newRest)) {
        oldIndex = restIndex;
      }
    }

    const newIndex = newEg.restPeriods[0].eventGroup;
    const oldValue = oldHotels.find((hotel) => hotel.restIndex === (oldIndex ?? newIndex));
    const newValue = newHotels.find((hotel) => hotel.restIndex === newIndex);
    // Store event group index of rest period if it is actually dirty.
    if (oldValue?.hotel?._id !== newValue?.hotel?._id) {
      dirtyIndexes.push(newIndex);
    }
  });

  return dirtyIndexes;
};

/**
 * return the rest NFAs that have a dirty hotel
 * @param pairing-
 * @returns an array of nfa _ids that are dirty
 */
export const onDutyRestHotelIsDirty = (pairing: IPairing): string[] => {
  const dirtyNFAs: string[] = [];

  // HOTELS
  const oldRestNFAs: INonFlyingAssignment[] = [];
  pairing?.eventGroups?.forEach((eventGroup) => {
    eventGroup.nonFlyingAssignments?.forEach((nfa) => {
      if (isINonFlyingAssignment(nfa) && nfa.assignmentType?.isOnDutyRest) {
        oldRestNFAs.push(nfa);
      }
    });
  });

  const newRestNFAs: INonFlyingAssignment[] = [];
  pairing?.eventGroups?.forEach((eventGroup) => {
    eventGroup.nonFlyingAssignments?.forEach((nfa) => {
      if (isINonFlyingAssignment(nfa) && nfa.assignmentType?.isOnDutyRest) {
        newRestNFAs.push(nfa);
      }
    });
  });

  oldRestNFAs.forEach((nfa) => {
    if (newRestNFAs.find((newNFA) => newNFA._id === nfa._id)?.custom?.hotel_id !== nfa?.custom?.hotel_id) {
      dirtyNFAs.push(nfa._id);
    }
  });

  return dirtyNFAs;
};

/**
 * PAIRING EDITOR COMPONENT
 */
export const PairingEditorWindow = (props: IPairingEditorWindowProps): JSX.Element => {
  const { windowProps, id, path, setCloseWarning } = props;

  const { pairingId, crewMember, selectedTime, flights } = windowProps;
  const dispatch: any = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const [fetchingPairingError, setFetchingPairingError] = useState<boolean>(false);

  const openEditors = useSelector((state: IRootState) => state.crewSchedule.pairingEditors);
  const pairingEditorIsRemoved = useMemo(() => {
    return openEditors[pairingId] ? false : true;
  }, [pairingId, openEditors]);

  // If flights are passed in through props, track whether they have been assigned to pairing or not.
  const [propFlightsAssigned, setPropFlightsAssigned] = useState<boolean>(false);

  const [newSplitLM, setNewSplitLM] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [refreshDue, setRefreshDue] = useState<boolean>(false);
  const [pairing, setPairing] = useState<IPairing>();
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [iteration, setIteration] = useState<number>(0);
  const [tabValue, setTabValue] = useState<string>("CrewPositions");
  const [selectedRow, setSelectedRow] = useState(null);
  // "Flight Duty Period Display" toggle, survives the remount a save/refresh triggers
  const [flightDutyPeriodDisplay, setFlightDutyPeriodDisplay] = useState<boolean>(false);

  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[pairingId]);

  const [splitLayout, setSplitLayout] = useState<boolean>(false);
  const pairingMosaic: HTMLElement | null = id ? document.getElementById(`${id}-reparentable`) : null;
  const [activeModal, setActiveModal] = useState<IActiveModal>();

  const [pairingErrors, setPairingErrors] = useState<IStatusCheckIssue[]>([]);

  // Embedded form initial values...
  const [embeddedFormStates, setEmbeddedFormStates] = useState<IEmbeddedFormState>(embeddedFormDefaultState);
  const [nonFlyingAssignment, setNonFlyingAssignment] = useState<INonFlyingAssignment>(newNonFlyingAssignment);
  const [positioningFlight, setPositioningFlight] = useState<IPositioningFlight>(newPositioningFlight);
  const [logicModifier, setLogicModifier] = useState<IPairingLogicModifier>();
  // Switch display of all times in the pairing display to UTC or local.
  const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(false);
  const positions = useSelector((state: IRootState) => state.structures.customerStructure.positions);
  // Set default position as captain (used when creating new pairings)
  const defaultPosition: IFullPairingPosition = useMemo(
    () => ({ crew: null, position: positions.find((pos) => pos.title === "CA") }) as IFullPairingPosition,
    [positions]
  );

  const creditSetting = useSelector((state: IRootState) => state.credit.settings) ?? [];

  const defaultPlannedCreditMap = useMemo<IPlannedCreditMap | undefined>(() => {
    return creditSetting.length > 0 ? getDefaultPlannedCreditMap(creditSetting) : undefined;
  }, [creditSetting]);

  const hotels: IPairingHotel[] = Array.isArray(storedPairing?.hotels) ? storedPairing?.hotels : [];
  const restPeriodIndexDirty = restPeriodIsDirty(pairing, storedPairing);

  const positionsEqual = (initPositions: IFullPairingPosition[], newPositions: IFullPairingPosition[]): boolean => {
    if (!initPositions || !newPositions || initPositions?.length !== newPositions?.length) return false;
    let same = true;
    for (let n = 0; n < initPositions.length; n++) {
      if (
        initPositions[n]?.crew?._id !== newPositions[n]?.crew?._id ||
        initPositions[n]?.openFlyingStatus !== newPositions[n]?.openFlyingStatus ||
        !_.isEqual(initPositions[n]?.position, newPositions[n]?.position)
      ) {
        same = false;
        break;
      }
    }
    return same;
  };

  const positionsTabDirty = useMemo(() => {
    return (
      !positionsEqual(pairing?.positions, storedPairing?.positions) ||
      !_.isEqual(pairing?.isBiddable, storedPairing?.isBiddable) ||
      !_.isEqual(pairing?.crewNote, storedPairing?.crewNote) ||
      (isNaN(pairing?.plannedCredit) && !isNaN(storedPairing?.plannedCredit)) ||
      (!isNaN(pairing?.plannedCredit) && !_.isEqual(pairing?.plannedCredit, storedPairing?.plannedCredit))
    );
  }, [pairing, storedPairing]);

  const dirty = useMemo(() => {
    const notEqual = !_.isEqual(storedPairing, pairing);
    if (notEqual && !refreshDue) {
      setRefreshDue(true);
    }
    return notEqual;
  }, [storedPairing]);

  // Display form state of all subforms used inside the editor.
  const { pairingNotesFormDirty, nonFlyingFormDirty, posFlightFormDirty, logicModifierFormDirty } = embeddedFormStates;

  const updateEventGroups = useCallback(
    async (eventGroups: IPairingEventGroup[]) => {
      setIsSubmitting(true);
      try {
        const loadedGroups = await loadMissingFlights(eventGroups);
        if (!loadedGroups) {
          dispatch(
            openSnackbar({
              open: true,
              closeDelay: null,
              variant: "error",
              message: "Error: Unable to load a flight's schedule, so the pairing was not changed.",
            })
          );
          return;
        }
        let hasEvents = false;
        loadedGroups.forEach((group: IPairingEventGroup) => {
          if (
            group?.flights?.length > 0 ||
            group.nonFlyingAssignments?.length > 0 ||
            group.positioningFlights?.length > 0 ||
            group.groundEvents?.length > 0
          ) {
            hasEvents = true;
          }
        });
        const updatedPairing = hasEvents
          ? (await mergeDraft({ ...storedPairing, eventGroups: loadedGroups }, dispatch))?.pairing
          : { ...storedPairing, eventGroups: loadedGroups };
        if (updatedPairing) {
          dispatch(updatePairingEditor(pairingId, updatedPairing));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [storedPairing, pairingId, pairing]
  );

  useEffect(() => {
    if (!pairing || !storedPairing) return;
    let nfaChanged = false;
    let posChanged = false;
    if (newSplitLM) {
      const eventGroups = _.cloneDeep(storedPairing?.eventGroups);
      updateEventGroups(eventGroups);
      setNewSplitLM(false);
    }
    for (let n = 0; n < storedPairing.eventGroups.length; n++) {
      if (
        !_.isEqual(storedPairing.eventGroups[n].nonFlyingAssignments, pairing.eventGroups[n]?.nonFlyingAssignments) &&
        storedPairing.eventGroups[n]?.nonFlyingAssignments.length > 0
      )
        nfaChanged = true;
      if (
        !_.isEqual(storedPairing.eventGroups[n].positioningFlights, pairing.eventGroups[n]?.positioningFlights) &&
        storedPairing.eventGroups[n]?.positioningFlights.length > 0
      )
        posChanged = true;
      if (nfaChanged && posChanged) break;
    }
    if (setEmbeddedFormStates) {
      setEmbeddedFormStates({
        logicModifierFormDirty: !_.isEqual(storedPairing?.logicModifiers, pairing?.logicModifiers),
        pairingNotesFormDirty: pairing?.pairingNotes.length !== storedPairing?.pairingNotes.length,
        posFlightFormDirty: posChanged,
        nonFlyingFormDirty: nfaChanged,
      });
    }
  }, [storedPairing]);

  const handleUpdateHotels = useCallback(
    (newHotels: IPairingHotel[]): void => {
      dispatch(updatePairingEditor(pairingId, { hotels: newHotels }));
    },
    [storedPairing]
  );
  const isNew = storedPairing?._id?.includes("new");
  const headerHeight = "auto";
  const useOverflow = false;

  // Boolean value that determines whether or not to prompt user for addition of incoming prop flights.
  const showPendingPropFlights = !pairingId.includes("new") && !propFlightsAssigned && flights?.length > 0;

  // Use to determine if components should be displayed in full width or split horizontally.
  useEffect(() => {
    if (pairingMosaic) {
      const { width } = pairingMosaic.getBoundingClientRect();
      setSplitLayout(width > 1400);
    }
  }, [pairingMosaic, path]);

  useEffect(() => {
    // Specify how to clean up after this effect:
    return () => {
      dispatch(removeTemporaryPairing(pairingId));
      dispatch(setSelectedCrewEvents([], false, false, false));
    };
  }, [dispatch, pairingId]);

  useEffect(() => {
    return () => {
      dispatch(removePairingEditor(pairingId));
    };
  }, [dispatch, pairingId]);

  // CREATE NEW PAIRING
  useEffect(() => {
    if (!pairing && pairingId.includes("new")) {
      // Set default start/end time to that of the selectedTime's day.
      // This will be trimmed/expanded as soon as events are added to the pairing.
      const pairingTime = selectedTime ? selectedTime : new Date();
      const start = moment.utc(pairingTime).startOf("day")?.valueOf();
      const end = moment.utc(pairingTime).endOf("day")?.valueOf();
      // If crew is passed in through props, add those to new pairing.
      const crewPosition = crewMember?.crewInfo?.positions[0]
        ? [{ crew: crewMember, position: crewMember.crewInfo.positions[0] }]
        : [defaultPosition];
      // If flights are passed in through props, add those to new pairing.
      const eventGroups: IPairingEditorEventGroup[] =
        flights?.length > 0 ? [{ ...defaultEventGroup, flights } as IPairingEditorEventGroup] : [];
      const newPairingInfo: Partial<IPairing> = {
        _id: pairingId,
        start: start,
        end: end,
        positions: crewPosition,
        eventGroups,
        //add the planned credit map if that settings exists
        plannedCreditMap: defaultPlannedCreditMap,
      };
      const newPairing: IPairing = createTempPairing(newPairingInfo);
      setPairing(newPairing);
      dispatch(addPairingEditor(newPairing));
    }
  }, [pairing, pairingId, crewMember, selectedTime, defaultPosition, flights, dispatch, defaultPlannedCreditMap]);

  // Get or refresh pairing.
  const getPairingAction = useCallback(
    async (refresh?: boolean): Promise<void> => {
      if (refresh) setRefreshing(true); // only set true if there's already a pairing loaded (will force display of spinner.)
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `${refresh ? "Refreshing" : "Loading"} Pairing...`,
        successMessage: `Pairing Successfully ${refresh ? "Refreshed" : "Loaded"}`,
        errorMessage: `Error: Unable to ${refresh ? "Refresh" : "Load"} Pairing`,
      };
      const loadedPairing = await AmplifyAPIWrapper.Get<IPairing | any>(`/pairings/${pairingId}`, notifications);
      if (loadedPairing?._id) {
        setPairing(loadedPairing);
        dispatch(addPairingEditor(loadedPairing));
      }
      if (loadedPairing?.err) {
        setFetchingPairingError(true);
      }
      setIteration(iteration + 1);
      setRefreshing(false);
    },
    [pairingId, dispatch]
  );

  /** Apply what the admin Recalculate Credit action wrote. */
  const applyRecalculatedCredit = useCallback((result: ICalculatePlannedCreditResult): void => {
    if (!result?.written || Object.keys(result.written).length < 1) {
      return;
    }
    // A merge through the functional updater, not a replace, so an edit made while the call was in flight
    // survives it. Only the local pairing is written: the effect below mirrors every change to the store's
    // pairing editor, and a second dispatch merging over a captured storedPairing would only add a stale
    // base that the mirror then overwrote anyway.
    setPairing((current) => ({
      ...current,
      calculatedPlannedCredit: { ...current?.calculatedPlannedCredit, ...result.written },
    }));
  }, []);

  // EDIT EXISTING PAIRING
  useEffect(() => {
    if ((!pairing || (pairing?._id && pairingId && pairing?._id !== pairingId)) && !pairingId.includes("new")) {
      getPairingAction();
    } else {
      dispatch(updatePairingEditor(pairing?._id, pairing));
    }
  }, [pairing, pairingId, getPairingAction]);

  // Does event have errors
  const rowHasError = useCallback(
    (eventId: string): boolean => {
      const found = pairingErrors.find((error: IStatusCheckIssue) => error.data?._id === eventId);
      return found ? true : false;
    },
    [pairingErrors]
  );
  //does rest have an error
  const restHasError = useCallback(
    (index: number): boolean => {
      const found = pairingErrors.find((error: IStatusCheckIssue) => {
        return error.data?.eventGroup === index;
      });
      return found ? true : false;
    },
    [pairingErrors]
  );

  const submit = useCallback(async (): Promise<void> => {
    setIsSubmitting(true);
    const values = validatePositionOverrides(storedPairing);
    const mode = values?._id?.includes("new") ? "create" : "update";
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Pairing...`,
      successMessage: `Pairing Successfully ${mode === "update" ? "Updated" : "Created"}.`,
      errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Pairing`,
    };

    const draftResponse = await mergeDraft(values, dispatch);
    if (!draftResponse) {
      setIsSubmitting(false);
      return;
    }
    const draftPairing: IPairing = draftResponse.pairing;
    const { _id, ...newPairing } = draftPairing;
    const errorLength = draftResponse.errors.length;
    if (errorLength > 0) {
      setPairingErrors(draftResponse.errors);
      setActiveModal({
        title: `Pairing Error${errorLength > 1 ? "s" : ""} (${errorLength})`,
        message: "",
        cancel: () => {
          setPairingErrors([]);
          setActiveModal(undefined);
          setIsSubmitting(false);
        },
        confirm: async (): Promise<void> => {
          setActiveModal(undefined);
          setPairingErrors([]);
          if (mode === "create") {
            const response = await AmplifyAPIWrapper.Post<IPairing>("/pairings", newPairing, notifications);
            if (response?._id) {
              dispatch(
                updateTabNodeByWindowIdAndProps(
                  id,
                  {
                    title: "Edit Pairing",
                    props: {
                      pairingId: response._id,
                    },
                  },
                  windowProps
                )
              );
              dispatch(updatePairingEditor(pairing._id, pairing));
              dispatch(removeTemporaryPairing(_id));
              dispatch(setSelectedCrewEvents([], false, false, false));
            }
            setPairing(response);
            setIsSubmitting(false);
          } else {
            // Update
            const response = await AmplifyAPIWrapper.Put<IPairing>(
              `/pairings/${values._id}`,
              draftPairing,
              notifications
            );
            if (response?._id) {
              //! after successful update - remove temporary pairing from crew(s) row
              dispatch(updatePairingEditor(pairing._id, pairing));
              dispatch(removeTemporaryPairing(values._id));
              dispatch(setSelectedCrewEvents([], false, false, false));
            }
            setPairing(response);
            setIsSubmitting(false);
          }
        },
        confirmLabel: "Update",
        list: draftResponse.errors?.map((error) => error.description),
      });
    } else {
      if (mode === "create") {
        // Create
        const response = await AmplifyAPIWrapper.Post<IPairing>("/pairings", newPairing, notifications);
        if (response?._id) {
          dispatch(addPairingEditor(response));
          dispatch(removePairingEditor(pairingId));
          //! Update mosaic window props to transform window to an edit if pairing successfully created.
          dispatch(
            updateTabNodeByWindowIdAndProps(
              id,
              {
                title: "Edit Pairing",
                props: {
                  pairingId: response?._id,
                },
              },
              windowProps
            )
          );
          //! after successful create - remove temporary pairing from crew(s) row
          dispatch(updatePairingEditor(pairing._id, pairing));
          dispatch(removeTemporaryPairing(_id));
          dispatch(setSelectedCrewEvents([], false, false, false));
        }
        setPairing(response);
        setIsSubmitting(false);
      } else {
        // Update
        const response = await AmplifyAPIWrapper.Put<IPairing>(`/pairings/${values._id}`, draftPairing, notifications);
        if (response?._id) {
          //! after successful update - remove temporary pairing from crew(s) row
          dispatch(updatePairingEditor(pairing._id, pairing));
          dispatch(removeTemporaryPairing(values._id));
          dispatch(setSelectedCrewEvents([], false, false, false));
        }
        setPairing(response);
        setIsSubmitting(false);
      }
    }
    setRefreshDue(true);
  }, [storedPairing]);

  const handleSelectTab = (event: any, newValue: string): void => {
    if (refreshDue && newValue == "CrewPositions") setIteration(iteration + 1);
    setTabValue(newValue);
    setRefreshDue(false);
  };

  const handleEditEvent = (type: string, editEvent: IPairingEvent, newEvent?: boolean): void => {
    if (type === "nonFlight") {
      setNonFlyingAssignment({ ...(editEvent as INonFlyingAssignment) });
      setTabValue("NonFlyingAssignments");
    } else if (type === "posFlight") {
      setPositioningFlight({ ...(editEvent as IPositioningFlight) });
      setTabValue("PositioningFlights");
    }
  };

  const updateLogicModifier = (logicModifier?: IPairingLogicModifier): void => {
    setLogicModifier(logicModifier);
  };

  /**
   * Load in appropriate tab content depending on which editor tab is currently selected.
   */
  const tabContent = useCallback(() => {
    const handleUpdateLogicModifier = (incomingLogicModifier: IPairingLogicModifier, isNew: boolean): void => {
      const newLogicModifiers = storedPairing.logicModifiers ? [...storedPairing.logicModifiers] : [];
      if (isNew) {
        newLogicModifiers.push(incomingLogicModifier);
      } else {
        const index = newLogicModifiers.findIndex((cur: IPairingLogicModifier) => cur._id === logicModifier?._id);
        newLogicModifiers.splice(index, 1, incomingLogicModifier);
      }
      dispatch(updatePairingEditor(pairingId, { logicModifiers: newLogicModifiers }));
      if (incomingLogicModifier?.logicModifier?.custom?.isSplitDuty) {
        setNewSplitLM(true);
      }
      setLogicModifier(undefined);
    };

    // update selected event with new form values.
    const handleUpdateEvent = (updatedEvent: IPairingEvent, eventType: string): void => {
      const eventGroups = _.cloneDeep(storedPairing?.eventGroups);
      if (!updatedEvent || !eventType || !eventGroups) {
        return;
      }

      let itemIndex = -1;
      switch (eventType) {
        // nonFlyingAssignments
        case "nonFlight":
          for (const group of eventGroups) {
            itemIndex = group.nonFlyingAssignments.findIndex(
              (currentEvent: INonFlyingAssignment) =>
                (!!currentEvent._id && currentEvent._id === updatedEvent._id) ||
                (!!currentEvent._tempId && currentEvent._tempId === (updatedEvent as INonFlyingAssignment)._tempId)
            );
            // If found, edit it, and break out of for loop.
            if (itemIndex !== -1) {
              group.nonFlyingAssignments[itemIndex] = updatedEvent as INonFlyingAssignment;
              break;
            }
          }

          // If not found, add nonflight to first group, otherwise edit it.
          if (itemIndex === -1 && !!eventGroups[0]?.nonFlyingAssignments) {
            eventGroups[0].nonFlyingAssignments.push(updatedEvent as INonFlyingAssignment);
          } else if (itemIndex === -1 && eventGroups.length === 0) {
            const newEventGroup: IPairingEventGroup = {
              ...defaultEventGroup,
              nonFlyingAssignments: [updatedEvent as INonFlyingAssignment],
            };
            eventGroups.push(newEventGroup);
          }
          break;

        // positioningFlights
        case "posFlight":
          for (const group of eventGroups) {
            itemIndex = group.positioningFlights.findIndex(
              (currentEvent: IPositioningFlight) =>
                (!!currentEvent._id && currentEvent._id === updatedEvent._id) ||
                (!!currentEvent._tempId && currentEvent._tempId === (updatedEvent as IPositioningFlight)._tempId)
            );
            // If found, edit it, and break out of for loop.
            if (itemIndex !== -1) {
              group.positioningFlights[itemIndex] = updatedEvent as IPositioningFlight;
              break;
            }
          }

          // If not found, add posflight to first group, otherwise edit it.
          if (itemIndex === -1 && !!eventGroups[0]?.positioningFlights) {
            eventGroups[0].positioningFlights.push(updatedEvent as IPositioningFlight);
          } else if (itemIndex === -1 && eventGroups.length === 0) {
            const newEventGroup: IPairingEventGroup = {
              ...defaultEventGroup,
              positioningFlights: [updatedEvent as IPositioningFlight],
            };
            eventGroups.push(newEventGroup);
          }
          break;

        default:
          break;
      }

      // Runs changes through draft pairing endpoint and updates form fields accordingly.
      updateEventGroups(eventGroups);
    };

    return (
      <Box>
        <div style={{ display: tabValue === "CrewPositions" ? "flex" : "none" }}>
          <CrewPositionsTab
            containerRef={containerRef}
            iteration={iteration}
            className={"tab_styles"}
            id={pairingId}
            windowId={id}
            path={path}
            initPairing={pairing}
            dirty={dirty}
            onCreditRecalculated={applyRecalculatedCredit}
          />
        </div>
        <div style={{ display: tabValue === "PairingNotes" ? "flex" : "none" }}>
          <PairingNotesTab
            id={pairingId}
            className={"tab_styles"}
            setEmbeddedFormStates={setEmbeddedFormStates}
            initPairing={pairing}
          />
        </div>
        <div style={{ display: tabValue === "NonFlyingAssignments" ? "flex" : "none" }}>
          <NonFlyingAssignmentsTab
            className={"tab_styles"}
            id={id}
            nonFlyingAssignment={nonFlyingAssignment}
            updateNonFlyingAssignment={handleUpdateEvent}
            setEmbeddedFormStates={setEmbeddedFormStates}
            setSelectedNonFlyingAssignment={setNonFlyingAssignment}
          />
        </div>
        <div style={{ display: tabValue === "PositioningFlights" ? "flex" : "none" }}>
          <PositioningFlightsTab
            className={"tab_styles"}
            id={id}
            positioningFlight={positioningFlight}
            updatePositioningFlight={handleUpdateEvent}
          />
        </div>
        <div style={{ display: tabValue === "LogicModifiers" ? "flex" : "none" }}>
          <LogicModifiersTab
            className={"tab_styles"}
            id={id}
            pairingId={pairingId}
            updateLogicModifier={handleUpdateLogicModifier}
            logicModifier={logicModifier}
            setSelectedLogicModifier={setLogicModifier}
            setEmbeddedFormStates={setEmbeddedFormStates}
            isUTC={displayAsUTC}
          />
        </div>
      </Box>
    );
  }, [
    tabValue,
    id,
    path,
    nonFlyingAssignment,
    selectedRow,
    positioningFlight,
    logicModifier,
    displayAsUTC,
    storedPairing,
    pairing,
    dirty,
    applyRecalculatedCredit,
  ]);
  // If flights are passed in from another component and displayed in the props, allow user to add them to eventgroups.
  const addFlightsFromWindowProps = useCallback(async () => {
    if (flights?.length > 0 && !!storedPairing) {
      const newFlights: IPairingEditorEventGroup = { ...defaultEventGroup, flights };
      const newEventGroups = [...storedPairing.eventGroups, newFlights];
      await updateEventGroups(newEventGroups);
      setPropFlightsAssigned(true);
    }
  }, [flights, storedPairing, updateEventGroups]);

  useEffect(() => {
    if (showPendingPropFlights && storedPairing) {
      addFlightsFromWindowProps();
    }
  }, [showPendingPropFlights, storedPairing, addFlightsFromWindowProps]);

  if (fetchingPairingError || (pairing && pairingEditorIsRemoved)) {
    return (
      <Typography variant="body2" style={{ padding: 15 }}>
        The pairing you were editing was not found in the crew schedule data. Please reload the crew schedule display
        and attempt to edit again
      </Typography>
    );
  }
  return (
    <Box sx={(theme) => pairingEditorWindowStyles(theme)} className={"pairing_editor_window"} key={iteration}>
      {activeModal && (
        <DialogContent className={"ModalDialogStyle"}>
          <div className="modal_content" style={{ display: "flex", maxWidth: 500 }}>
            <ModalTitle title={activeModal.title} />
            <ModalList descrption={activeModal.message} list={activeModal.list} />
            <DialogActions>
              <ModalButton label="Cancel" handler={activeModal.cancel}></ModalButton>
              <ModalButton label={activeModal.confirmLabel} handler={activeModal.confirm}></ModalButton>
            </DialogActions>
          </div>
        </DialogContent>
      )}
      <div id="editorBody">
        {(!pairing || !storedPairing || isSubmitting || refreshing) && (
          <Box
            sx={
              pairing
                ? {
                    position: "absolute",
                    inset: 0,
                    zIndex: 500,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: (theme) => alpha(theme.palette.background.default, 0.75),
                  }
                : { width: "100%", display: "flex", justifyContent: "center" }
            }
          >
            <SpinnerProgress
              loading={!pairing || isSubmitting || refreshing}
              message={refreshing ? "Refreshing Pairing" : "Validating Event Groups"}
              fullscreen={false}
            />
          </Box>
        )}
        <WarningModal displayModal={dirty} setCloseWarning={setCloseWarning} />
        {pairing && storedPairing && (
          <Grid container className={`content_container ${splitLayout ? "split" : ""}`}>
            <Grid
              item
              className="content_pairing_tabs"
              style={{ height: headerHeight, overflowY: useOverflow ? "auto" : "unset" }}
            >
              <Grid container direction="column" wrap="nowrap" style={{ width: "100%" }}>
                <Grid item>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <div className="form_actions" style={{ width: isNew ? "142px" : "204px" }}>
                      {dirty || isNew ? (
                        <Button
                          className="larger"
                          variant="contained"
                          color="secondary"
                          disabled={!dirty}
                          onClick={_.debounce(() => getPairingAction(true), 500)}
                        >
                          Reset
                        </Button>
                      ) : (
                        <Button
                          className="larger"
                          variant="outlined"
                          onClick={_.debounce(() => getPairingAction(true), 500)}
                        >
                          Refresh
                        </Button>
                      )}

                      <Button
                        className="larger"
                        onClick={submit}
                        variant="contained"
                        color="primary"
                        disabled={!dirty || !storedPairing || storedPairing.eventGroups?.length < 1 || isSubmitting}
                      >
                        {isNew ? "Create" : "Update"}
                      </Button>
                      {!isNew && (
                        <ShortCodeChip
                          className="shortCode_chip"
                          code={pairing?.shortId}
                          itemId={pairing?._id}
                          type="pairing"
                        />
                      )}
                    </div>
                    <OcTabs
                      className="form_tabs"
                      style={{ width: isNew ? "calc(100% - 142px)" : "calc(100% - 204px)" }}
                      variant="scrollable"
                      orientation="horizontal"
                      scrollButtons="auto"
                      value={tabValue}
                      onChange={handleSelectTab}
                    >
                      <Tab
                        wrapped={true}
                        value="CrewPositions"
                        label="Crew Positions"
                        classes={{ root: classNames("form-tab", positionsTabDirty && "dirty") }}
                        icon={<EditRounded />}
                      />
                      <Tab
                        wrapped={true}
                        value="PairingNotes"
                        label="Notes"
                        classes={{ root: classNames("form-tab", pairingNotesFormDirty && "dirty") }}
                        icon={<EditRounded />}
                      />
                      <Tab
                        wrapped={true}
                        value="NonFlyingAssignments"
                        label="Non-Flying Assignments"
                        classes={{ root: classNames("form-tab", nonFlyingFormDirty && "dirty") }}
                        icon={<EditRounded />}
                      />
                      <Tab
                        wrapped={true}
                        value="PositioningFlights"
                        label="Positioning Flights"
                        classes={{ root: classNames("form-tab", posFlightFormDirty && "dirty") }}
                        icon={<EditRounded />}
                      />
                      <Tab
                        wrapped={true}
                        value="LogicModifiers"
                        label="Logic Modifiers"
                        classes={{ root: classNames("form-tab", logicModifierFormDirty && "dirty") }}
                        icon={<EditRounded />}
                      />
                    </OcTabs>
                  </div>
                </Grid>

                <Grid item>
                  <Card className="pairing_cards">
                    <div>{tabContent()}</div>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid item className={classNames("content_pairing_table", dirty && "dirty")}>
              <Grid container direction="column">
                <Grid item>
                  <Card className="pairing_cards eventgroups_card">
                    <div id="eventGroups">
                      <PairingEventTable
                        id={pairingId}
                        editEvent={handleEditEvent}
                        selectedTableRow={(row: any) => setSelectedRow(row)}
                        selectedLM={logicModifier}
                        updateLogicModifier={updateLogicModifier}
                        hotels={hotels}
                        updateHotels={handleUpdateHotels}
                        restPeriodIndexDirty={restPeriodIndexDirty}
                        restHasError={restHasError}
                        rowHasError={rowHasError}
                        displayAsUTC={displayAsUTC}
                        setDisplayAsUTC={setDisplayAsUTC}
                        initPairing={pairing}
                        updateEventGroups={updateEventGroups}
                        flightDutyPeriodDisplay={flightDutyPeriodDisplay}
                        setFlightDutyPeriodDisplay={setFlightDutyPeriodDisplay}
                      />
                    </div>

                    <CopyToClipboard
                      label={`Pairing ID: ${pairing?._id}`}
                      copyValue={pairing?._id}
                      className={"eventId"}
                    />
                    <CopyToClipboard
                      label={`Batch ID: ${pairing?.batchId}`}
                      copyValue={pairing?.batchId}
                      className={"eventId"}
                    />
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </div>
    </Box>
  );
};
